import { useEffect, useState } from 'react'

import axios from 'axios'
import mixpanel from 'mixpanel-browser'
import useWebSocket from 'react-use-websocket'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useTagContext } from '../../../contexts/tagContext'
import { useUserContext } from '../../../contexts/userContext'
import { usePannellumViewer } from '../../../hooks/usePannellum'
import { useImageViewerContext } from '../../views/image_viewer/imageViewerContext'
import { ViewerPosition } from '../../views/image_viewer/types'

import { ChatHeader } from './components/ChatHeader'
import { ChatHistory } from './components/ChatHistory'
import { ChatInput } from './components/ChatInput'
import { ChatUserSelect } from './components/ChatUserSelect'

const BASE_URL = process.env.REACT_APP_SERVICES_API
const BASE_WS = process.env.REACT_APP_SERVICES_WS

const ChatDialogue = styled.div`
  position: fixed;
  z-index: 100;
  width: 350px;
  height: 300px;
  background: #fff;
  border-radius: 2px;
`

interface ChatManagerProps {
  viewerId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper hydrate type
  hydrate: any
  viewerPosition: ViewerPosition
}

export const ChatManager = ({
  viewerId,
  hydrate,
  viewerPosition,
}: ChatManagerProps) => {
  const buildingState = useBuildingContext().state
  const { state: ImageViewerState } = useImageViewerContext()
  const { findSetCurrentTag, state: tagState } = useTagContext()
  const { state: userState } = useUserContext()
  const { addNotification } = useNotifications()
  const [transform, setTransform] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper messages type
  const [messages, setMessages] = useState<any>([])
  const [chatRoomUUID, setChatRoomUUID] = useState<string | null>(null)
  const { viewer } = usePannellumViewer(viewerId)
  const currentTag = useTagContext().state.tags.current
  const chatState = useTagContext().state.chat

  /*
  Copied from Pannellum to keep the dialogue in correct place in "3D" space
   */
  const renderDialogue = () => {
    const chatDom = document.getElementById('chat-dialogue')
    if (chatDom && viewer) {
      const ctya = currentTag.yaw,
        canvasPitch = viewerPosition.pitch,
        canvasYaw = viewerPosition.yaw - ImageViewerState.master.data.angle,
        canvasHfov = viewerPosition.hfov,
        hsPitchSin = Math.sin((currentTag.pitch * Math.PI) / 180),
        hsPitchCos = Math.cos((currentTag.pitch * Math.PI) / 180),
        configPitchSin = Math.sin((canvasPitch * Math.PI) / 180),
        configPitchCos = Math.cos((canvasPitch * Math.PI) / 180),
        yawCos = Math.cos(((-ctya + canvasYaw) * Math.PI) / 180)
      const z =
        hsPitchSin * configPitchSin + hsPitchCos * yawCos * configPitchCos
      if (
        (ctya <= 90 && ctya > -90 && z <= 0) ||
        ((ctya > 90 || ctya <= -90) && z <= 0)
      ) {
        // eslint-disable-next-line no-console -- TODO: Remove debug console.log
        console.log('no visible')
      } else {
        const yawSin = Math.sin(((-ctya + canvasYaw) * Math.PI) / 180),
          hfovTan = Math.tan((canvasHfov * Math.PI) / 360)
        // Subpixel rendering doesn't work in Firefox
        // https://bugzilla.mozilla.org/show_bug.cgi?id=739176
        const canvas = viewer.getRenderer().getCanvas(),
          canvasWidth = canvas.clientWidth,
          canvasHeight = canvas.clientHeight
        const coord = [
          ((-canvasWidth / hfovTan) * yawSin * hsPitchCos) / z / 2,
          ((-canvasWidth / hfovTan) *
            (hsPitchSin * configPitchCos -
              hsPitchCos * yawCos * configPitchSin)) /
            z /
            2,
        ]
        // Apply transform
        coord[0] += (canvasWidth - chatDom.offsetWidth + 300) / 2
        coord[1] += (canvasHeight - chatDom.offsetHeight - 360) / 2
        const t =
          'translate(' + coord[0] + 'px, ' + coord[1] + 'px) translateZ(9999px)'
        setTransform(t)
      }
    }
  }

  const onCreateConversation = (users: string[]) => {
    const room_uuid = uuidv4()
    axios
      .post(`${BASE_URL}/tags/`, {
        creator: userState.public_id,
        pitch: currentTag.pitch,
        yaw: currentTag.yaw,
        project_id: buildingState.projectId,
        point_id: buildingState.pointId,
        floor_id: buildingState.floorId,
        image_id: buildingState.imageData.data.id,
        type: 'CHAT',
        room_uuid: room_uuid,
        users: users.join(),
      })
      .then(res => {
        hydrate().then(() => {
          findSetCurrentTag(res.data.id)
          setChatRoomUUID(room_uuid)
        })
        mixpanel.track('Create Chat', { ...res.data })
      })
      // eslint-disable-next-line no-console -- TODO: Replace with proper error handling
      .catch(() => console.log)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  const onMessage = (e: any) => {
    const data = JSON.parse(e.data)
    if (data.event_type === 'hydrate') {
      const sortedMessages = data.messages
        .sort(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper message types
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .reverse()
      setMessages(sortedMessages)
      const messageData = {
        sender: userState.public_id,
        event_type: 'reception',
      }
      sendMessage(JSON.stringify(messageData))
    } else if (data.event_type === 'chat_message') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper prevState type
      setMessages((prevState: any) => {
        return prevState.concat(data.message)
      })
      const messageData = {
        sender: userState.public_id,
        event_type: 'reception',
      }
      sendMessage(JSON.stringify(messageData))
    }
  }

  const { sendMessage } = useWebSocket(
    `${BASE_WS}/chat/${chatRoomUUID}/`,
    {
      onOpen: () => {
        // eslint-disable-next-line no-console -- TODO: Remove debug console.log
        console.log(chatRoomUUID)
        mixpanel.track('Join Chat', {
          room: chatRoomUUID,
        })
      },
      onMessage: onMessage,
      onError: () =>
        addNotification('Error connecting to chat server', 'error'),
      onReconnectStop: () =>
        addNotification('Could not connect to chat server', 'error'),
      shouldReconnect: () => true,
      reconnectAttempts: 3,
      reconnectInterval: 1000,
      retryOnError: true,
    },
    !!chatRoomUUID,
  )

  useEffect(() => {
    if (currentTag) {
      if (currentTag.type === 'CHAT') {
        if (currentTag.chat_room) setChatRoomUUID(currentTag.chat_room.uuid)
      }
    }
  }, [currentTag])

  useEffect(() => {
    renderDialogue()
  })

  return (
    <>
      {tagState.displayDialogue && currentTag.type === 'CHAT' && (
        <ChatDialogue
          style={{ transform: transform }}
          id="chat-dialogue"
          className="down-arrow pnlm-ignore pnlm-ignore oco-dialogue"
        >
          {chatState.status === 'create' && (
            <ChatUserSelect onCreate={onCreateConversation} />
          )}
          {chatState.status === 'conversation' && (
            <>
              <ChatHeader hydrate={hydrate} />
              <ChatHistory messages={messages} />
              <ChatInput sendMessage={sendMessage} viewerId={viewerId} />
            </>
          )}
        </ChatDialogue>
      )}
    </>
  )
}
